import { loadAccountData } from 'store/customer/customer-view-actions';
import { moveSelected } from 'utils/utils';
import isEmpty from 'lodash/isEmpty';
import Affiliations from '../affiliations/Affiliations';
import SiteAffiliations from '../affiliations/SiteAffiliation';
import Engagements from '../engagements/Engagements';
import Insights from '../insights/Insights';
import PayerMix from '../payer-mix/PayerMix';
import ProductMix from '../product-mix/ProductMix';
import SitePerformance from '../site-performance/SitePerformance';
import SiteUtilization from '../site-utilization/SiteUtilization';
import MenuItem from './MenuItem';
import PolicyDetails from '../mco/policy-detail/PolicyDetails';
import CopayAnalytics from '../copay-analytics/CopayAnalytics';

export const loadOptions = async (
  offset,
  options,
  selected,
  type,
  ecosystem,
  prevEcosystem,
  searchQuery,
  loadMore,
  unixId,
  isMyPriority,
  dropdownAction,
  searchOptions
) => {
  let data = [];
  let hasMore = false;
  let listSize = 0;

  /**
    1.use the existing option list if
    user made a selection, is not search,
    ecosystem has not changed and hasn't reach the dropdown height.
    2. use the existing search option list if
    user made a selection, is search,
    ecosystem has not changed and hasn't reach the dropdown height.
  **/
  if (
    selected?.length > 0 &&
    !searchQuery &&
    ecosystem?.toString() === prevEcosystem?.toString() &&
    options?.length > 0 &&
    !loadMore
  ) {
    data = [...move(selected, options)];
  } else if (
    selected?.length > 0 &&
    ecosystem?.toString() === prevEcosystem?.toString() &&
    searchOptions.length > 0 &&
    !loadMore &&
    searchQuery &&
    dropdownAction === 'set-value'
  ) {
    data = [...move(selected, searchOptions)];
  } else {
    let { values, hasMoreElement, currentListSize } = await getData(
      type,
      ecosystem,
      offset,
      searchQuery,
      selected,
      unixId,
      isMyPriority,
      loadMore
    );

    hasMore = hasMoreElement;
    data = [...values];
    listSize = currentListSize;
  }

  return {
    data: data,
    hasMore: hasMore,
    currentListSize: listSize,
  };
};

export const getData = async (
  type,
  ecosystem,
  offset,
  searchQuery,
  selected,
  unixId,
  isMyPriority,
  loadmore
) => {
  let data = [];

  const { values, currentListSize, hasMoreElement } = await loadAccountData({
    offset: offset,
    searchToken: searchQuery,
    filed: type,
    ecosystems: ecosystem,
    unixId,
    isMyPriority,
  });

  // move the selected options to the top of the list
  if ((selected?.length > 0 || !isEmpty(selected)) && !loadmore) {
    const opts = moveSelected(selected, values);
    data = [...opts];
  } else {
    data = [...values];
  }

  return { values: data, currentListSize, hasMoreElement };
};

export const shouldLoadMoreOptions = (
  scrollHeight,
  clientHeight,
  scrollTop
) => {
  const bottomBorder = (scrollHeight - clientHeight) / 2;
  return bottomBorder < scrollTop;
};

const move = (selected, options) => {
  if (selected?.length > 0 && options?.length > 0) {
    const opts = moveSelected(selected, options);
    return opts;
  } else if (options.length > 0) {
    return options;
  }

  return options;
};

export const getSelectedDimension = (dimensions, selectedDimensionName) => {
  let defaultDimension = dimensions?.filter(
    (dm) => dm.dimensionName === selectedDimensionName
  )?.[0];
  return defaultDimension;
};

export const searchResult = (result = [], searchText, field = 'label') => {
  const options = result?.filter((item) =>
    item[field]?.toLowerCase()?.includes(searchText?.toLowerCase())
  );
  return options;
};

export const getHCOMenuItems = (selectedView, componentAccessList, roleId) => [
  {
    key: 'Insights',
    shouldRender: true,
    component: <Insights />,
    label: (
      <MenuItem
        href='#Insights'
        label='Insights'
      />
    ),
  },
  {
    key: 'Engagements',
    shouldRender: true,
    component: <Engagements />,
    label: (
      <MenuItem
        href='#Engagements'
        label='Engagements'
      />
    ),
  },
  {
    key: 'SitePerformance',
    shouldRender:
      selectedView === 'account' &&
      componentAccessList?.includes('Site-Performance'),
    component: <SitePerformance />,
    label: (
      <MenuItem
        href='#SitePerformance'
        label='Site Performance'
      />
    ),
  },
  {
    key: 'ProductMix',
    shouldRender:
      selectedView === 'site' && componentAccessList?.includes('Product-Mix'),
    component: <ProductMix />,
    label: (
      <MenuItem
        href='#ProductMix'
        label='Product Mix'
      />
    ),
  },
  {
    key: 'PayerMix',
    shouldRender: componentAccessList?.includes('Payer-Mix-Table'),
    component: <PayerMix />,
    label: (
      <MenuItem
        href='#PayerMix'
        label='Payer Mix'
      />
    ),
  },
  {
    key: 'SiteUtilization',
    shouldRender: componentAccessList?.includes('Site-Utilization'),
    component: <SiteUtilization />,
    label: (
      <MenuItem
        href='#SiteUtilization'
        label='Site Utilization & Demographics'
      />
    ),
  },
  {
    key: 'Affiliations',
    shouldRender: selectedView === 'account',
    component: (
      <Affiliations
        data={selectedView}
        role={roleId}
      />
    ),
    label: (
      <MenuItem
        href='#Affiliations'
        label='Affiliations'
      />
    ),
  },
  {
    key: 'SiteAffiliations',
    shouldRender: selectedView === 'site',
    component: (
      <SiteAffiliations
        data={selectedView}
        role={roleId}
        filterExp=''
        enableTitle={true}
        tableType={'siteView'}
      />
    ),
    label: (
      <MenuItem
        href='#SiteAffiliations'
        label='Affiliations'
      />
    ),
  },
  {
    key: 'CopayAnalytics',
    shouldRender: componentAccessList?.includes('CopayAnalytics'),
    component: (
      <div id='CopayAnalytics'>
        <CopayAnalytics />
      </div>
    ),
    label: (
      <MenuItem
        href='#CopayAnalytics'
        label='Copay Analytics'
      />
    ),
  },
];

export const getMCOMenuItems = (selectedView, componentAccessList, roleId) => [
  {
    key: 'policyDetails_parentpayer',
    shouldRender: selectedView === 'parent payer',
    component: (
      <div id='policyDetails_parentpayer'>
        <PolicyDetails
          data={selectedView}
          role={roleId}
        />
      </div>
    ),
    label: (
      <MenuItem
        href='#policyDetails_parentpayer'
        label='Policy Details'
      />
    ),
  },
  {
    // Note : Sperated section menu items with same componet just to toggle PolicyDetails compoent to its default state while switching b/w payer customers
    key: 'policyDetails_payer',
    shouldRender: selectedView === 'payer',
    component: (
      <div id='policyDetails_payer'>
        <PolicyDetails
          data={selectedView}
          role={roleId}
        />
      </div>
    ),
    label: (
      <MenuItem
        href='#policyDetails_payer'
        label='Policy Details'
      />
    ),
  },
];

export const getNewActiveKey = (menuItems, activeKey) => {
  let newActiveKey = activeKey;
  const targetTopPosition = 100;
  const scrollTop = window.scrollY;
  for (let i = 0; i < menuItems.length; i++) {
    const item = menuItems[i];
    const section = document.getElementById(item.key);
    if (!section) continue;
    const rect = section.getBoundingClientRect();
    if (i === 0 && scrollTop === 0) {
      newActiveKey = item.key;
      break;
    }

    if (i < menuItems.length - 1 && rect.top <= targetTopPosition) {
      newActiveKey = item.key;
    }
    if (
      i === menuItems.length - 1 &&
      (rect.top <= targetTopPosition ||
        (rect.top < window.innerHeight && rect.bottom <= window.innerHeight))
    ) {
      newActiveKey = item.key;
    }
  }
  return newActiveKey;
};
